/**
 * UnifiedChat - Main chat container with integrated voice mode.
 *
 * Combines:
 * - MessageList for chat messages
 * - ChatInput with file upload support
 * - VoiceOverlay for unified voice mode
 * - Real-time transparency system
 *
 * This replaces the monolithic Chat.tsx with a component-based architecture.
 */

import { useState, useCallback } from 'react'
import { useChatStore, type Message } from '@/store/chatStore'
import { sendMessageWithStreaming } from '@/api/chat'
import { MessageList } from './ChatArea/MessageList'
import { ChatInput } from './Input/ChatInput'
import type { FileAttachment } from './Input/FilePreviewChip'
import { VoiceOverlay } from './Voice/VoiceOverlay'
import SessionList from './SessionList'

export function UnifiedChat() {
  const [input, setInput] = useState('')

  const {
    messages,
    addMessage,
    setLoading,
    isLoading,
    sessionId,
    setSessionId,
    loadSessions,
    sessionVersion,
    streamingStatus,
    streamingToolCalls,
    setStreamingStatus,
    addStreamingToolCall,
    updateStreamingToolCall,
    clearStreamingState,
    voiceOverlayOpen,
    setVoiceOverlayOpen,
  } = useChatStore()

  const handleSubmit = useCallback(
    async (attachments?: FileAttachment[]) => {
      if (!input.trim() && (!attachments || attachments.length === 0)) return
      if (isLoading) return

      // Build user message with optional attachments
      // Note: File attachments will be sent to backend when file upload API is implemented
      // For now, we include attachment metadata in the message for display purposes
      const userMessage: Message = {
        id: crypto.randomUUID(),
        role: 'user',
        content: input.trim(),
        timestamp: new Date(),
        // Include attachment info for display (actual upload handled separately)
        attachments: attachments?.map((a) => ({
          id: a.id,
          filename: a.file.name,
          type: (a.file.type.startsWith('image/')
            ? 'image'
            : a.file.type === 'application/pdf'
              ? 'pdf'
              : 'text') as 'image' | 'pdf' | 'text',
          size: a.file.size,
          url: a.preview || '',
          mimeType: a.file.type,
        })),
      }

      // Capture the session version at the time of sending
      const requestSessionVersion = sessionVersion
      const requestSessionId = sessionId

      addMessage(userMessage)
      setInput('')
      setLoading(true)
      clearStreamingState()

      try {
        const response = await sendMessageWithStreaming(
          {
            message: userMessage.content,
            session_id: requestSessionId || undefined,
          },
          {
            onStatus: (status) => {
              if (useChatStore.getState().sessionVersion === requestSessionVersion) {
                setStreamingStatus(status)
              }
            },
            onToolStart: (name, args) => {
              if (useChatStore.getState().sessionVersion === requestSessionVersion) {
                addStreamingToolCall({
                  name,
                  arguments: args,
                  status: 'running',
                })
              }
            },
            onToolResult: (name, result, success) => {
              if (useChatStore.getState().sessionVersion === requestSessionVersion) {
                updateStreamingToolCall(name, {
                  result,
                  status: success ? 'success' : 'error',
                })
              }
            },
            onThought: (thought) => {
              if (useChatStore.getState().sessionVersion === requestSessionVersion) {
                setStreamingStatus(`Thinking: ${thought}`)
              }
            },
          }
        )

        // Check if session changed while waiting for response
        const currentState = useChatStore.getState()
        if (currentState.sessionVersion !== requestSessionVersion) {
          console.log('Session changed during request, discarding stale response')
          setLoading(false)
          clearStreamingState()
          return
        }

        if (!requestSessionId) {
          setSessionId(response.session_id)
          loadSessions()
        }

        const assistantMessage: Message = {
          id: crypto.randomUUID(),
          role: 'assistant',
          content: response.message.content,
          timestamp: new Date(),
          toolCalls: response.tool_calls?.map((tc) => ({
            name: tc.name,
            arguments: tc.arguments,
            result: tc.result,
            status: 'success' as const,
            auditLogId: tc.audit_log_id,
          })),
          suggestedActions: response.suggested_actions,
        }

        addMessage(assistantMessage)
      } catch (_error) {
        const currentState = useChatStore.getState()
        if (currentState.sessionVersion === requestSessionVersion) {
          addMessage({
            id: crypto.randomUUID(),
            role: 'assistant',
            content: 'Sorry, something went wrong. Please try again.',
            timestamp: new Date(),
          })
        }
      } finally {
        setLoading(false)
        clearStreamingState()
      }
    },
    [
      input,
      isLoading,
      sessionVersion,
      sessionId,
      addMessage,
      setLoading,
      clearStreamingState,
      setStreamingStatus,
      addStreamingToolCall,
      updateStreamingToolCall,
      setSessionId,
      loadSessions,
    ]
  )

  const handleVoiceTranscript = useCallback(
    (text: string, role: 'user' | 'assistant', isFinal: boolean) => {
      if (isFinal) {
        addMessage({
          id: crypto.randomUUID(),
          role,
          content: text,
          timestamp: new Date(),
          isVoice: true,
        })
      }
    },
    [addMessage]
  )

  // Handle suggestion click - set input and submit immediately
  const handleSuggestionClick = useCallback(
    (text: string) => {
      setInput(text)
      // Use setTimeout to ensure state update is processed before submit
      setTimeout(() => {
        // Directly submit the suggestion text
        const userMessage: Message = {
          id: crypto.randomUUID(),
          role: 'user',
          content: text,
          timestamp: new Date(),
        }

        const requestSessionVersion = sessionVersion
        const requestSessionId = sessionId

        addMessage(userMessage)
        setInput('')
        setLoading(true)
        clearStreamingState()

        sendMessageWithStreaming(
          {
            message: text,
            session_id: requestSessionId || undefined,
          },
          {
            onStatus: (status) => {
              if (useChatStore.getState().sessionVersion === requestSessionVersion) {
                setStreamingStatus(status)
              }
            },
            onToolStart: (name, args) => {
              if (useChatStore.getState().sessionVersion === requestSessionVersion) {
                addStreamingToolCall({
                  name,
                  arguments: args,
                  status: 'running',
                })
              }
            },
            onToolResult: (name, result, success) => {
              if (useChatStore.getState().sessionVersion === requestSessionVersion) {
                updateStreamingToolCall(name, {
                  result,
                  status: success ? 'success' : 'error',
                })
              }
            },
            onThought: (thought) => {
              if (useChatStore.getState().sessionVersion === requestSessionVersion) {
                setStreamingStatus(`Thinking: ${thought}`)
              }
            },
          }
        ).then((response) => {
          const currentState = useChatStore.getState()
          if (currentState.sessionVersion !== requestSessionVersion) {
            setLoading(false)
            clearStreamingState()
            return
          }

          if (!requestSessionId) {
            setSessionId(response.session_id)
            loadSessions()
          }

          addMessage({
            id: crypto.randomUUID(),
            role: 'assistant',
            content: response.message.content,
            timestamp: new Date(),
            toolCalls: response.tool_calls?.map((tc) => ({
              name: tc.name,
              arguments: tc.arguments,
              result: tc.result,
              status: 'success' as const,
              auditLogId: tc.audit_log_id,
            })),
            suggestedActions: response.suggested_actions,
          })
        }).catch(() => {
          const currentState = useChatStore.getState()
          if (currentState.sessionVersion === requestSessionVersion) {
            addMessage({
              id: crypto.randomUUID(),
              role: 'assistant',
              content: 'Sorry, something went wrong. Please try again.',
              timestamp: new Date(),
            })
          }
        }).finally(() => {
          setLoading(false)
          clearStreamingState()
        })
      }, 0)
    },
    [
      sessionVersion,
      sessionId,
      addMessage,
      setLoading,
      clearStreamingState,
      setStreamingStatus,
      addStreamingToolCall,
      updateStreamingToolCall,
      setSessionId,
      loadSessions,
    ]
  )

  return (
    <div className="flex h-full min-h-0">
      {/* Session sidebar */}
      <SessionList />

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-2 sm:p-4">
          <MessageList
            messages={messages}
            isLoading={isLoading}
            streamingStatus={streamingStatus}
            streamingToolCalls={streamingToolCalls}
            onSuggestionClick={handleSuggestionClick}
          />
        </div>

        {/* Input */}
        <ChatInput
          value={input}
          onChange={setInput}
          onSubmit={handleSubmit}
          onVoiceClick={() => setVoiceOverlayOpen(true)}
          isLoading={isLoading}
          showVoiceButton={true}
        />
      </div>

      {/* Voice overlay */}
      <VoiceOverlay
        isOpen={voiceOverlayOpen}
        onClose={() => setVoiceOverlayOpen(false)}
        onTranscript={handleVoiceTranscript}
      />
    </div>
  )
}

export default UnifiedChat
